import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Plus, Trash2, FileText, Pencil } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";

const statusStyle = {
  APPROVED: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  PENDING: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  IN_APPEAL: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  REJECTED: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300",
  DISABLED: "border-neutral-500/40 bg-neutral-500/10 text-muted-foreground",
  PAUSED: "border-neutral-500/40 bg-neutral-500/10 text-muted-foreground",
};

export default function Templates() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/templates")
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/templates/sync");
      const { synced, meta_total, skipped_deleted, skipped_tombstoned, pages } = r.data || {};
      toast.success(
        `Synced ${synced} of ${meta_total ?? synced} from Meta` +
        (skipped_deleted ? ` · ${skipped_deleted} DELETED skipped` : "") +
        (skipped_tombstoned ? ` · ${skipped_tombstoned} tombstoned` : "") +
        (pages > 1 ? ` · ${pages} pages` : "")
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (name) => {
    try {
      const r = await api.delete(`/templates/${name}`);
      if (r.data?.meta_deleted === false) {
        toast.warning(`Hidden locally — Meta rejected deletion: ${r.data.meta_error || "unknown"}`);
      } else {
        toast.success("Template deleted");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-10 py-10">
      <header className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">WABA</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="templates-title">
            Message Templates
          </h1>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            Draft and submit new templates to Meta for approval. Only <span className="text-emerald-600 dark:text-emerald-400 font-semibold">APPROVED</span> templates can be sent to customers.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sync} disabled={syncing} className="rounded-sm" data-testid="sync-templates-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync from Meta"}
          </Button>
          <Button asChild className="rounded-sm" data-testid="new-template-btn">
            <Link to="/templates/new">
              <Plus className="mr-2 h-4 w-4" /> New template
            </Link>
          </Button>
        </div>
      </header>

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Name</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Category</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Language</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Status</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.2em]">Body</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">No templates yet</div>
                    <Button asChild className="rounded-sm">
                      <Link to="/templates/new"><Plus className="mr-2 h-4 w-4" /> Create your first template</Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((t) => {
                const body = (t.components || []).find((c) => (c.type || "").toUpperCase() === "BODY")?.text || "—";
                return (
                  <TableRow key={t.name} className="border-border" data-testid={`template-row-${t.name}`}>
                    <TableCell className="font-mono font-medium">{t.name}</TableCell>
                    <TableCell><span className="text-xs uppercase tracking-widest text-muted-foreground">{t.category}</span></TableCell>
                    <TableCell className="font-mono text-xs">{t.language}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${statusStyle[t.status] || ""}`}>
                        {t.status || "LOCAL"}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md truncate text-sm text-muted-foreground">{body}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {user?.role === "admin" && (
                          <Button asChild variant="ghost" size="icon" data-testid={`edit-template-${t.name}`} aria-label="Edit">
                            <Link to={`/templates/edit/${t.name}`}>
                              <Pencil className="h-4 w-4 text-primary" />
                            </Link>
                          </Button>
                        )}
                        {user?.role === "admin" && (
                          <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-template-${t.name}`} aria-label="Delete">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-sm">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete template &quot;{t.name}&quot;?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This removes the template from Meta and from your CRM. You cannot recover it.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(t.name)} className="rounded-sm bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
